
import type React from "react"
import { useState, useEffect } from "react"
import { useTheme } from "../contexts/ThemeContext"
import { useAuth } from "../hooks/useAuth"
import { useTranslation } from "../hooks/useTranslation"
import { useCountryConfig } from "../hooks/useCountryConfig"
import {
  getPhoneInputConfig,
  isValidPhoneNumber,
  sanitizePhoneNumber,
} from "../config/countryConfig"

interface PhoneNumberPopupProps {
  onClose: () => void
  onSubmit: (phoneNumber: string) => void
}

const PhoneNumberPopup: React.FC<PhoneNumberPopupProps> = ({ onClose, onSubmit }) => {
  const { t, currentLanguage: language } = useTranslation()
  const { primaryColor } = useTheme()
  const { login, isLoading, error: authError, clearError } = useAuth()
  const countryConfig = useCountryConfig()
  const phoneRegion = countryConfig.phoneRegion || 'SA'
  const phoneConfig = getPhoneInputConfig(phoneRegion)
  const [phoneNumber, setPhoneNumber] = useState("")
  const [error, setError] = useState("")
  const [isVisible, setIsVisible] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Animation effect
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 10)
    return () => clearTimeout(timer)
  }, [])

  // Clear auth error when component mounts
  useEffect(() => {
    clearError()
  }, [clearError])

  // Close with animation
  const handleClose = () => {
    setIsVisible(false)
    setTimeout(() => {
      onClose()
    }, 300) // Wait for animation to complete
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Prevent multiple submissions
    if (isSubmitting || isLoading) {
      return
    }

    if (!phoneNumber.trim()) {
      setError(language === "ar" ? "يرجى إدخال رقم الهاتف" : "Please enter phone number")
      return
    }

    const cleanPhone = phoneNumber.replace(/\D/g, '')

    if (!isValidPhoneNumber(cleanPhone, phoneRegion)) {
      if (phoneConfig.startsWith && !cleanPhone.startsWith(phoneConfig.startsWith)) {
        setError(
          language === "ar"
            ? `رقم الهاتف يجب أن يبدأ بـ ${phoneConfig.startsWith}`
            : `Phone number must start with ${phoneConfig.startsWith}`
        )
      } else {
        setError(
          language === "ar"
            ? `رقم الهاتف يجب أن يكون ${phoneConfig.minLength} رقم`
            : `Phone number must be ${phoneConfig.minLength} digits`
        )
      }
      return
    }

    try {
      setIsSubmitting(true)
      // Clear any previous errors
      setError("")
      clearError()


      // Call the login API with clean phone number
      await login(cleanPhone)


      // If successful, call the original onSubmit callback
      await onSubmit(cleanPhone)


      // Close the modal after successful login and onSubmit
      // This ensures the phone popup closes and returns to product modal
      handleClose()
    } catch (error: any) {
      // Handle login error
      const errorMessage = error.message || t('errors.loginFailed')
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div
        className={`bg-white w-full max-w-sm mx-4 rounded-2xl p-6 shadow-2xl transition-all duration-300 ease-out ${isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"
          }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          {/* Title */}
          <h2 className="text-xl font-bold text-gray-800">
            {t("auth.enterPhoneNumber")}
          </h2>
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="text-3xl rounded-xl w-10 h-10 flex items-center justify-center hover:bg-gray-100 transition-colors"
            style={{
              border: `1px solid #e5e7eb`
            }}
            disabled={isLoading || isSubmitting}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Phone Input */}
          <div className="mb-4">
          
            <input
              type="tel"
              dir={language === 'ar' ? "rtl" : "ltr"}
              value={phoneNumber}
              onChange={(e) => {
                setPhoneNumber(sanitizePhoneNumber(e.target.value, phoneRegion))
                setError("")
                clearError()
              }}
              className={`w-full border-1 ${(error || authError) ? 'border-red-500' : 'border-gray-300'} rounded-xl p-3 text-lg focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-transparent transition-colors`}
              style={{
                borderColor: (error || authError) ? '#ef4444' : '#d1d5db',
                backgroundColor: '#ffffff'
              }}
              maxLength={phoneConfig.maxLength}
              autoFocus
              disabled={isLoading || isSubmitting}
              pattern="[0-9]*"
              inputMode="numeric"
              placeholder={phoneConfig.placeholder}
            />
          </div>

          {/* Error Message */}
          {(error || authError) && (
            <p className="text-red-500 text-sm mb-4 text-center">
              {error || authError}
            </p>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || isSubmitting}
            className="w-full py-2 rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-white hover:opacity-90 transition-opacity text-lg"
            style={{ backgroundColor: primaryColor }}
          >
            {(isLoading || isSubmitting) ? (
              <>
               {t("common.loading")}
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </>
            ) : (
              t("auth.continue")
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

export default PhoneNumberPopup
